import IndexService from '@/service';
import { useUserStore } from '@/store/modules/user';
import { levelOptionList } from '@/config/robot';
import { ColorEnum } from '@/enums/chessEnum';
import { createPagination } from '@/utils/pagination';
import { useI18n } from '@/hooks/web/useI18n';
import { navigateTo } from '@/utils/page';
import UserService from '@/service/user';
import { withDebounceCache } from '@/utils/util';

const { t } = useI18n();

export const [requestNewsList, newsListDisabled, newsListLoading] = createPagination(
    IndexService.getNewList.bind(IndexService),
);

export const [requestGameRecord, gameRecordDisabled, gameRecordLoading] = createPagination(
    IndexService.getGameRecordV2.bind(IndexService),
);

export enum LiveAndNewsItemType {
    /**
     * 直播
     */
    live = 'live',
    /**
     * 新闻
     */
    news = 'news',
}

export enum SeeMoreType {
    /**
     * 直播
     */
    live = 'live',
    /**
     * 新闻
     */
    news = 'news',
    /**
     * 游戏记录
     */
    gameRecord = 'gameRecord',
}

export async function getGameRecordListV2(init = false) {
    const res = await requestGameRecord({}, init);
    const getCount = (data: any, gameResult: GameResult) =>
        data.reduce((ret: number, i: any) => {
            if (String(i.gameResult) === gameResult) {
                ret++;
            }
            return ret;
        }, 0);

    const getPveUserName = (level: any) => {
        return levelOptionList.reduce((p: any, c: any) => {
            if (c.value.level <= level) {
                p = c.label;
            }
            return p;
        }, t('components.game.level.easy'));
    };

    const retObj = {};
    // 将返回结果按照日期分组
    res.data.forEach((item) => {
        const key = item.date.split(' ')[0];
        if (!retObj[key]) {
            retObj[key] = [];
        }
        retObj[key].push({
            id: item.game_log_id,
            time: item.date,
            username:
                item.game_type == 4
                    ? `${getPveUserName(item.pve_level)}${t('common.robot')}`
                    : item.user_account.nickname,
            gameResult: item.game_result,
            fen: item.fen,
            gameLogId: item.game_log_id,
            color: item.piece_color === 'b' ? ColorEnum.BLACK : ColorEnum.WHITE,
            result: {
                1: '0-0',
                2: item.piece_color === 'b' ? '0-1' : '1-0',
                3: item.piece_color === 'b' ? '1-0' : '0-1',
                4: '½-½',
            }[item.game_result as GameResult],
        });
    });
    const ret: Array<GameRecordDateItem> = [];
    for (const key in retObj) {
        if (Object.prototype.hasOwnProperty.call(retObj, key)) {
            const item = retObj[key];
            ret.push({
                date: key,
                winCount: getCount(item, '2'),
                failCount: getCount(item, '3'),
                drawCount: getCount(item, '4'),
                total: item.length,
                list: item,
            });
        }
    }
    // 按照日期排序
    ret.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return ret;
}

export async function getGameRecordList() {
    const res = await IndexService.getGameRecord(
        {},
        {
            error: t('components.index.user.failToObtainGameRecord'),
        },
    );
    const getCount = (data: any, game_result: GameResult) =>
        data.reduce((ret: number, i: any) => {
            if (String(i.game_result) === game_result) {
                ret++;
            }
            return ret;
        }, 0);

    const getPveUserName = (level: any) => {
        return levelOptionList.reduce((p: any, c: any) => {
            if (c.value.level == level) {
                p = c.label;
            }
            return p;
        }, t('components.game.level.easy'));
    };
    return (
        (res.list as Array<any>)?.map(
            (item): GameRecordDateItem => ({
                date: item.date,
                winCount: getCount(item.data, '2'),
                failCount: getCount(item.data, '3'),
                drawCount: getCount(item.data, '4'),
                total: item.data.length,
                list: item.data.map(
                    (i: any): GameRecordItemType => ({
                        id: i.game_log_id,
                        time: i.date,
                        username:
                            i.game_type == 4
                                ? `${getPveUserName(i.pve_level)}${t('common.robot')}`
                                : i.user_account.nickname,
                        gameResult: i.game_result,
                        fen: i.fen,
                        gameLogId: i.game_log_id,
                        color: i.piece_color === 'b' ? ColorEnum.BLACK : ColorEnum.WHITE,
                        result: {
                            1: '0-0',
                            2: i.piece_color === 'b' ? '0-1' : '1-0',
                            3: i.piece_color === 'b' ? '1-0' : '0-1',
                            4: '½-½',
                        }[i.game_result as GameResult],
                    }),
                ),
            }),
        ) || []
    );
}

export async function getNewsList({ type, init = false }: { type?: boolean; init?: boolean }) {
    const res = await requestNewsList({ data: { type: type ? 2 : 1 } }, init);
    res.data = (res.data as Array<any>).map(
        (item: any): LiveOrNewsItem => ({
            id: item.id,
            content: item.title,
            thumbnailImage: item.image,
            source: item.source,
            date: item.release_time,
            jumpUrl: item.jump_url,
        }),
    );
    return res;
}

export function handleClickNews(data: JumpOption) {
    const handle = {
        webview: () => {
            function isProtocolMissing(url) {
                // 使用正则表达式检查链接是否缺少协议部分
                const protocolRegex = /^(https?|ftp):\/\//;
                return !protocolRegex.test(url);
            }
            let url = data.target;
            if (isProtocolMissing(data.target)) {
                url = '//' + data.target;
            }
            window.open(url);
        },
        page: () => {
            navigateTo({
                name: data.target,
                data: { id: data.option.params.id },
            });
        },
        mp: () => {
            console.log('mp');
        },
        null: () => {
            console.log('null');
        },
    }[data.type];
    handle && handle();
}

/**
 * 获取用户基本信息
 */
export const getBaseInfo = withDebounceCache(async () => {
    const userStore = useUserStore();
    const res = await UserService.getBaseInfo({
        user_id: userStore.userInfo.userId,
    });
    userStore.onlineSchoolStatus = !!res.online_school_status;
    return res;
}, 300);
